import { isUndefined } from 'trtc-js-sdk-core/src/utils/utils';
import { parse, write } from '../sdp';
import { NAME } from 'trtc-js-sdk-core';
import transform from 'sdp-transform';

export const getUplinkSSRCFromSDP = (sdp: string) => {
  const parsedSDP = parse(sdp);
  const result = {
    audioSsrc: 0,
    audioRtxSsrc: 0,
    bigVideoSsrc: 0,
    bigVideoRtxSsrc: 0,
    smallVideoSsrc: 0,
    smallVideoRtxSsrc: 0,
    auxVideoSsrc: 0,
    auxVideoRtxSsrc: 0
  };
  parsedSDP.media.forEach((item, index) => {
    if (item.ssrcs) {
      if (!isUndefined(item.ssrcs[0].id)) {
        const ssrc = Number(item.ssrcs[0].id);
        // 视频重传 ssrc
        const rtxSsrc = Number(item.ssrcs.filter(item => item.attribute === 'cname')[1]?.id);
        switch (index) {
          case 0:
            result.audioSsrc = ssrc;
            break;
          case 1:
            result.bigVideoSsrc = ssrc;
            result.bigVideoRtxSsrc = rtxSsrc;
            break;
          case 2:
            result.smallVideoSsrc = ssrc;
            result.smallVideoRtxSsrc = rtxSsrc;
            break;
          case 3:
            result.auxVideoSsrc = ssrc;
            result.auxVideoRtxSsrc = rtxSsrc;
            break;
        }
      }
    }
  });
  return result;
};

function parseSDPMedia(media: {
  type: string;
  port: number;
  protocol: string;
  payloads?: string | undefined;
} & transform.MediaDescription) {
  const result = [];
  for (let i = 0; i < media.rtp.length; i++) {
    if (['rtx', 'red', 'ulpfec'].includes(media.rtp[i].codec)) {
      continue;
    }
    const fmtp = media.fmtp.filter(item => item.payload === media.rtp[i].payload)[0];
    result.push({
      payload: media.rtp[i].payload,
      codec: media.rtp[i].codec,
      fmtp: fmtp ? fmtp.config : '',
      rate: media.rtp[i].rate,
      rtx: media.rtp[i + 1]?.codec === 'rtx' ? media.rtp[i + 1].payload : 0,
      rtcpfb: (media?.rtcpFb || [])
        .filter(item => item.payload === media.rtp[i].payload)
        .map(({ type, subtype }) => ({ id: type, params: subtype ? [subtype] : [] }))
    });
  }
  return result;
}

async function getDecodeAbility() {
  const peerConnection = new RTCPeerConnection();
  peerConnection.addTransceiver(NAME.VIDEO, {
    direction: NAME.TRANSCEIVER_DIRECTION_RECVONLY
  });
  const offer = await peerConnection.createOffer();
  if (!offer.sdp) return [];
  const parsedSDP = parse(offer.sdp);
  const decoders: AbilityVideoCodec[] = parseSDPMedia(parsedSDP.media[0]);
  peerConnection.close();
  return decoders;
}

/**
 * 解析 offer sdp，发给后台。
 */
export const getClientAbility = async (sdp: string, remove264FromSDP: boolean, enableDataChannel: boolean, enableDtx?: boolean) => {
  const parsedSDP = parse(sdp);
  const data: ClientAbility = {
    ice: {
      ufrag: '',
      password: ''
    },
    dtls: {
      hash: '',
      fingerprint: '',
      setup: '' // active actpass passive, sdk 默认服务端
    },
    audio: {
      codecs: [],
      extensions: []
    },
    video: {
      codecs: [],
      decoders: [],
      extensions: []
    },
    useDataChannel: enableDataChannel,
    useDtx: enableDtx
  };
  // 数字型的 iceUfrag 会被 sdp transform 解析成 number 类型，后台期望是收到 string 类型，否则会进房失败。
  // 此处强制转为 string 类型
  data.ice.ufrag = String(parsedSDP.media[0].iceUfrag);
  data.ice.password = parsedSDP.media[0].icePwd || '';
  if (parsedSDP.fingerprint) {
    data.dtls.hash = parsedSDP.fingerprint.type;
    data.dtls.fingerprint = parsedSDP.fingerprint.hash;
    data.dtls.setup = parsedSDP.setup || '';
  }
  if (parsedSDP.media[0].fingerprint) {
    data.dtls.hash = parsedSDP.media[0].fingerprint.type;
    data.dtls.fingerprint = parsedSDP.media[0].fingerprint.hash;
  }
  data.dtls.setup = parsedSDP.media[0].setup || '';
  const audioItem = parsedSDP.media[0];
  const videoItem = parsedSDP.media[1];
  if (audioItem.ext) {
    data.audio.extensions = audioItem.ext.map(item => ({ id: item.value, uri: item.uri }));
  }
  if (videoItem.ext) {
    data.video.extensions = videoItem.ext.map(item => ({ id: item.value, uri: item.uri }));
  }

  for (const rtpItem of audioItem.rtp) {
    if (rtpItem.codec !== 'opus') continue;
    const fmtp = audioItem.fmtp.find(fmtpItem => fmtpItem.payload === rtpItem.payload);
    if (!fmtp) continue;
    const audioCodec: AbilityAudioCodec = {
      codec: rtpItem.codec,
      fmtp: fmtp.config,
      payload: fmtp.payload,
      rate: rtpItem.rate,
      channels: rtpItem.encoding,
      rtcpfb: [],
      rtx: 0
    };
    if (enableDtx !== undefined) {
      audioCodec.fmtp = applyDTXToFmtp(audioCodec.fmtp, enableDtx);
    }
    audioItem.rtcpFb?.forEach(({ payload, type, subtype }) => {
      if (payload === audioCodec.payload) {
        const rtcpFbItem: { id: string, params: string[] } = { id: type, params: [] };
        if (subtype) rtcpFbItem.params.push(subtype);
        audioCodec.rtcpfb.push(rtcpFbItem);
      }
    });
    data.audio.codecs.push(audioCodec);
    break;
  }

  const videoEncoderList = ['h264', 'vp8', 'h265'];
  if (remove264FromSDP) videoEncoderList.shift();
  data.video.codecs = [...parseSDPMedia(videoItem)].filter(item => videoEncoderList.includes(item.codec.toLocaleLowerCase()));
  data.video.decoders = (await getDecodeAbility()).filter(item => ['h264', 'vp8', 'h265'].includes(item.codec.toLocaleLowerCase()));
  return data;
};

/**
 * 将后台返回的 answer sdp config 转成 answer sdp
 */
export interface ProfileLevelIdConfig {
  big?: string | string[];
  small?: string | string[];
  aux?: string | string[];
}

/**
 * 修改 fmtp 字符串中的 profile-level-id
 */
export const patchFmtpProfileLevelId = (fmtp: string, profileLevelId: string): string => {
  const trimmed = (fmtp || '').trim();
  const value = (profileLevelId || '').trim();
  const key = 'profile-level-id';
  const pattern = `${key}=[0-9a-fA-F]{6}`;
  const testRe = new RegExp(pattern);
  if (testRe.test(trimmed)) {
    const replaceRe = new RegExp(pattern, 'g');
    return trimmed.replace(replaceRe, `${key}=${value}`);
  }
  if (!trimmed) return `${key}=${value}`;
  const sep = trimmed.endsWith(';') ? '' : ';';
  return `${trimmed}${sep}${key}=${value}`;
};

export const getAnswerSDPFromServerAbility = ({ serverAbility, clientAbility, offerSDP, enableCustomMessage, profileLevelIdConfig }: { serverAbility: ServerAbility, clientAbility: ClientAbility; offerSDP: string, enableCustomMessage: boolean, profileLevelIdConfig?: ProfileLevelIdConfig }) => {
  const parsedOffer = parse(offerSDP);
  const data: transform.SessionDescription = {
    // @ts-ignore
    extmapAllowMixed: 'extmap-allow-mixed',
    groups: parsedOffer.groups,
    icelite: 'ice-lite',
    media: [],
    msidSemantic: { semantic: '', token: 'WMS' },
    name: '-',
    origin: {
      address: '127.0.0.1',
      username: '-',
      sessionId: String(Date.now()),
      sessionVersion: 1,
      netType: 'IN',
      ipVer: 4
    },
    timing: { start: 0, stop: 0 },
    version: 0
  };

  const audio: transform.MediaDescription = {
    candidates: serverAbility.candidates.map(item => ({
      component: 1,
      foundation: '1',
      generation: 0,
      ip: item.ip,
      port: item.port,
      priority: item.priority,
      transport: item.foundation,
      type: item.type
    })),
    connection: { version: 4, ip: '0.0.0.0' },
    direction: NAME.TRANSCEIVER_DIRECTION_RECVONLY as transform.MediaDescription['direction'],
    ext: serverAbility.audio.extensions.map(item => ({ value: item.id, uri: item.uri })),
    fingerprint: { type: serverAbility.dtls.hash, hash: serverAbility.dtls.fingerprint },
    fmtp: [{ payload: serverAbility.audio.codecs[0].payload, config: serverAbility.audio.codecs[0].fmtp }],
    icePwd: serverAbility.ice.password,
    iceUfrag: serverAbility.ice.ufrag,
    mid: '0',
    payloads: String(serverAbility.audio.codecs[0].payload),
    port: parsedOffer.media[0].port,
    protocol: parsedOffer.media[0].protocol,
    type: NAME.AUDIO,
    setup: serverAbility.dtls.setup,
    rtcpFb: serverAbility.audio.codecs[0].rtcpfb.map(item => ({
      payload: serverAbility.audio.codecs[0].payload,
      type: item.id,
      subtype: item.params[0]
    })),
    rtcpMux: 'rtcp-mux' as const,
    rtcpRsize: 'rtcp-rsize' as const,
    rtp: [
      {
        payload: serverAbility.audio.codecs[0].payload,
        codec: serverAbility.audio.codecs[0].codec,
        rate: serverAbility.audio.codecs[0].rate,
        encoding: serverAbility.audio.codecs[0].channels
      }
    ]
  };

  data.media.push(audio);
  // 固定三路上行视频描述：mid 1=big, 2=small, 3=aux
  const profileLevelIds = [profileLevelIdConfig?.big, profileLevelIdConfig?.small, profileLevelIdConfig?.aux];
  [1, 2, 3].forEach((mid, index) => {
    data.media.push(getVideoMediaConfig({ mid, serverAbility, clientAbility, parsedOffer, profileLevelId: profileLevelIds[index] }));
  });
  if (enableCustomMessage) {
    const dcMedia = findDataChannelMedia(parsedOffer);
    if (dcMedia) {
      // 构造 answer SDP 中的 DataChannel m-section，使用服务端的 ICE/DTLS 参数。
      // 不能直接复制 offer 的 DataChannel media，因为 offer 中的 setup/iceUfrag/icePwd/fingerprint
      // 是客户端的值，answer 中应该是服务端的值，否则 Firefox 会崩溃。
      const answerDcMedia = {
        ...dcMedia,
        candidates: serverAbility.candidates.map(item => ({
          component: 1,
          foundation: '1',
          generation: 0,
          ip: item.ip,
          port: item.port,
          priority: item.priority,
          transport: item.foundation,
          type: item.type
        })),
        iceUfrag: serverAbility.ice.ufrag,
        icePwd: serverAbility.ice.password,
        fingerprint: { type: serverAbility.dtls.hash, hash: serverAbility.dtls.fingerprint },
        setup: serverAbility.dtls.setup
      };
      data.media.push(answerDcMedia);
    }
  }
  return write(data);
};

/**
 * 获取 video media config，用于生成 answer sdp 中的 video 相关描述。
 * @param {Object} options
 * @param {number} options.mid
 * @param {Object} options.serverAbility 后台给的 ability 对象
 * @param {Object} options.parsedOffer 使用 sdp-transform 库解析出的 offer sdp 对象
 * @param {Object} [options.isDownlink = false] 是否是远端的 sdp
 * 1. 若是获取 answer 中的上行 video media config，则设置为 false。上行的 video codec 根据后台的 useVp8 参数来选择对应的 codec。
 * 2. 若是获取 answer 中的下行 video media config，则设置为 true。下行的 video codec 描述使用后台返回的所有 codec 配置。
 * @param {string | string[]} [options.profileLevelId] 可选的 H.264 profile-level-id，如果是字符串则覆盖 codec 中的值；如果是数组则从 offer 中提取对应 profile 的所有 H264 pm=1 codec
 * @returns
 */
export const getVideoMediaConfig = ({ mid, serverAbility, clientAbility, parsedOffer, isDownlink = false, profileLevelId }:
  { mid: number, serverAbility: ServerAbility, clientAbility: ClientAbility; parsedOffer: transform.SessionDescription, isDownlink?: boolean, profileLevelId?: string | string[] }) => {
  const video: TransformMediaSession = {
    candidates: serverAbility.candidates.map(item => ({
      component: 1,
      foundation: '1',
      generation: 0,
      ip: item.ip,
      port: item.port,
      priority: item.priority,
      transport: item.foundation,
      type: item.type
    })),
    connection: { version: 4, ip: '0.0.0.0' },
    direction: NAME.TRANSCEIVER_DIRECTION_RECVONLY as transform.MediaDescription['direction'],
    ext: serverAbility.video.extensions.map(item => ({ value: item.id, uri: item.uri })),
    fingerprint: { type: serverAbility.dtls.hash, hash: serverAbility.dtls.fingerprint },
    fmtp: [],
    icePwd: serverAbility.ice.password,
    iceUfrag: serverAbility.ice.ufrag,
    mid: String(mid),
    payloads: '',
    port: parsedOffer.media[0].port,
    protocol: parsedOffer.media[0].protocol,
    type: NAME.VIDEO,
    setup: serverAbility.dtls.setup,
    rtcpFb: [],
    rtcpMux: 'rtcp-mux' as const,
    rtcpRsize: 'rtcp-rsize' as const,
    rtp: []
  };

  if (isDownlink) {
    // 下行 offer & answer sdp 中都使用后台返回的 decoders 信息，sdp 中可能会同时包含 264 和 vp8，浏览器会根据实际收到的数据 payload type 来选择解码器。
    let decoderConfigList = serverAbility.video.decoders;
    if (!decoderConfigList || decoderConfigList.length === 0) decoderConfigList = serverAbility.video.codecs;
    if (!decoderConfigList || decoderConfigList.length === 0) decoderConfigList = clientAbility.video.decoders;
    // serverAbility.video.codecs 和 clientAbility.video.decoders 用于兜底，防止
    // 1. 后台没返回 decoders
    // 2. 浏览器环境不支持 h264 且没开通 vp8 白名单时，serverAbility 没有视频解码器信息。
    decoderConfigList.forEach(decoder => {
      initCodecConfig(video, decoder);
    });
  } else { // uplink
    let index;
    if (serverAbility.useH265) {
      index = serverAbility.video.codecs.findIndex(item => item.codec.toLowerCase() === 'h265');
    } else {
      index = serverAbility.video.codecs.findIndex(item => item.codec.toLowerCase() === (serverAbility.useVp8 ? 'vp8' : 'h264'));
    }
    const encoder = serverAbility.video.codecs[index] || clientAbility.video.codecs[0];
    initCodecConfig(video, encoder);
  }

  // 仅对上行生效：如果指定了 profileLevelId，修改 H264 codec 的 fmtp
  if (!isDownlink && profileLevelId) {
    if (Array.isArray(profileLevelId)) {
      const targetProfiles = new Set(profileLevelId.map(p => p.toLowerCase()));
      const targetCodecs = clientAbility.video.codecs.filter(codec => {
        if (codec.codec.toLowerCase() !== 'h264') return false;
        if (/packetization-mode=0/.test(codec.fmtp)) return false;
        const plid = extractProfileLevelIdFromFmtp(codec.fmtp);
        return plid && targetProfiles.has(plid);
      });
      if (targetCodecs.length > 0) {
        video.payloads = '';
        video.rtp = [];
        video.fmtp = [];
        video.rtcpFb = [];
        targetCodecs.forEach(codec => initCodecConfig(video, codec));
      }
    } else {
      const rtpList = video.rtp as { payload: number; codec: string; rate: number }[];
      const fmtpList = video.fmtp as { payload: number; config: string }[];
      const h264Rtp = rtpList.find(r => r.codec?.toLowerCase() === 'h264');
      if (h264Rtp) {
        const fmtpItem = fmtpList.find(f => String(f.payload) === String(h264Rtp.payload));
        if (fmtpItem) {
          fmtpItem.config = patchFmtpProfileLevelId(fmtpItem.config, profileLevelId);
        }
      }
    }
  }

  return video;
};

export const initCodecConfig = (videoMediaConfig: TransformMediaSession, codecConfig: AbilityVideoCodec | AbilityVideoCodec) => {
  videoMediaConfig.payloads = `${videoMediaConfig.payloads} ${codecConfig.payload}`.trim();
  videoMediaConfig.fmtp.push({
    payload: codecConfig.payload,
    config: codecConfig.fmtp
  });
  videoMediaConfig.rtcpFb = [
    ...(videoMediaConfig.rtcpFb || []),
    ...((codecConfig as AbilityVideoCodec).rtcpfb).map(item => ({
      payload: codecConfig.payload,
      type: item.id,
      subtype: item.params[0]
    }))
  ];
  videoMediaConfig.rtp.push({
    payload: codecConfig.payload,
    codec: codecConfig.codec.toUpperCase(),
    rate: codecConfig.rate
  });
  if (codecConfig.rtx) {
    videoMediaConfig.payloads = `${videoMediaConfig.payloads} ${codecConfig.rtx}`;
    videoMediaConfig.fmtp.push({
      payload: codecConfig.rtx,
      config: `apt=${codecConfig.payload}`
    });
    videoMediaConfig.rtp.push({
      payload: codecConfig.rtx,
      codec: 'rtx',
      rate: codecConfig.rate
    });
  }
};

/**
   * 删除如下 ext line
   * a=extmap:4 urn:ietf:params:rtp-hdrext:sdes:mid
   * a=extmap:5 urn:ietf:params:rtp-hdrext:sdes:rtp-stream-id
   * a=extmap:6 urn:ietf:params:rtp-hdrext:sdes:repaired-rtp-stream-id
   * 原因：不同浏览器对 extmap 解释不同，导致解包失败，进而导致黑屏。
   */
function removeSDESDescription(mediaItem: TransformMediaSession) {
  const extItemsToRemove = new Set([
    'urn:ietf:params:rtp-hdrext:sdes:mid',
    'urn:ietf:params:rtp-hdrext:sdes:rtp-stream-id',
    'urn:ietf:params:rtp-hdrext:sdes:repaired-rtp-stream-id'
  ]);
  if (mediaItem.ext) {
    mediaItem.ext = mediaItem.ext.filter(extItem => !extItemsToRemove.has(extItem.uri));
  }
}

/**
   * 给 sdp 增加 rrtr line
   * 规则：在 a=rtcp-fb:xxx 的最后一行追加 a=rtcp-fb:xxx rrtr
   * 作用：提升弱网抵抗能力
   */
function addRRTRLine(mediaItem: TransformMediaSession) {
  if (!mediaItem.rtcpFb) return;
  const rtcpFb: {
    payload: number | string;
    type: string;
    subtype?: string | undefined;
  }[] = [];
  mediaItem.rtcpFb.forEach((rtcpFbItem, index) => {
    rtcpFb.push(rtcpFbItem);
    if (mediaItem.rtcpFb && mediaItem.rtcpFb[index + 1]?.payload !== rtcpFbItem.payload && rtcpFbItem.type !== 'rrtr') {
      rtcpFb.push({ payload: rtcpFbItem.payload, type: 'rrtr' });
    }
  });
  mediaItem.rtcpFb = rtcpFb;
}

/**
   * 修复 Chrome h264 开启硬件加速，弱网丢包时拉流出现花屏的问题
   * ref: http://tapd.oa.com/TerWebG/prong/stories/view/1020396022860831661
   */
function addSPSDescription(mediaItem: TransformMediaSession) {
  if (mediaItem.type === NAME.VIDEO && mediaItem.fmtp) {
    mediaItem.fmtp.forEach(fmtpItem => {
      if (!fmtpItem.config.includes('apt')) {
        fmtpItem.config += ';sps-pps-idr-in-keyframe=1';
      }
    });
  }
}
/**
   * 给 sdp 添加立体声描述
   * sprop-stereo=1 上行立体声
   * stereo=1 下行立体声
   */
function addStereoDescription(mediaItem: TransformMediaSession) {
  if (mediaItem.type === NAME.AUDIO && mediaItem.fmtp) {
    mediaItem.fmtp.forEach(fmtpItem => {
      fmtpItem.config += ';sprop-stereo=1;stereo=1';
    });
  }
}

/**
 * 在 parsed SDP 的 media 列表中查找 DataChannel 对应的 m-section。
 * DataChannel 的 m-section 特征：type 为 'application' 或 payloads 包含 'datachannel'。
 */
function findDataChannelMedia(parsedSDP: transform.SessionDescription) {
  return parsedSDP.media.find(item => item.type === 'application' || item.payloads?.toString().includes('datachannel'));
}
function applyDTXToFmtp(fmtp: string, enableDtx?: boolean): string {
  if (isUndefined(enableDtx)) return fmtp;
  // Remove existing usedtx parameter
  let result = fmtp.replace(/;?usedtx=\d/g, '').replace(/^;/, '');
  if (enableDtx) {
    result += ';usedtx=1';
  }
  return result;
}

function addDTXDescription(mediaItem: TransformMediaSession, enableDtx: boolean) {
  if (mediaItem.type === NAME.AUDIO && mediaItem.fmtp) {
    mediaItem.fmtp.forEach(fmtpItem => {
      fmtpItem.config = applyDTXToFmtp(fmtpItem.config, enableDtx);
    });
  }
}

export const handleOfferSDP = (sdp: string, clientAbility: ClientAbility, serverAbility: ServerAbility | null) => {
  const parsedSDP = transform.parse(sdp);
  parsedSDP.media.forEach((mediaItem, index) => {
    if (mediaItem.type === NAME.AUDIO || mediaItem.type === NAME.VIDEO) {
      addRRTRLine(mediaItem);
      addSPSDescription(mediaItem);
      addStereoDescription(mediaItem);
      if (!isUndefined(clientAbility.useDtx)) {
        addDTXDescription(mediaItem, clientAbility.useDtx);
      }
      removeSDESDescription(mediaItem);
      // 设置 offer sdp 的 codec 信息。index 0 ～ 3 是上行的，其余都是下行的。
      if (mediaItem.type === NAME.VIDEO) {
        if (index < 4) {
          mediaItem.payloads = '';
          mediaItem.fmtp = [];
          mediaItem.rtp = [];
          mediaItem.rtcpFb = [];
          clientAbility.video.codecs.forEach(codec => initCodecConfig(mediaItem, codec));
        } else if (serverAbility) {
          mediaItem.payloads = '';
          mediaItem.fmtp = [];
          mediaItem.rtp = [];
          mediaItem.rtcpFb = [];
          let decoderConfigList = serverAbility.video.decoders;
          if (!decoderConfigList || decoderConfigList.length === 0) decoderConfigList = serverAbility.video.codecs;
          if (!decoderConfigList || decoderConfigList.length === 0) decoderConfigList = clientAbility.video.decoders;
          // serverAbility.video.codecs 和 clientAbility.video.decoders 用于兜底，防止
          // 1. 后台没返回 decoders
          // 2. 浏览器环境不支持 h264 且没开通 vp8 白名单时，serverAbility 没有视频解码器信息。
          decoderConfigList.forEach(codec => initCodecConfig(mediaItem, codec));
        }
      }
    }
    // Firefox 在 setLocalDescription 时严格校验 mid 不能被修改，
    // 因此不再在 offer SDP 中重命名 DataChannel 的 mid。
    // answer SDP 中通过 findDataChannelMedia() 查找 DataChannel m-section。
  });
  return transform.write(parsedSDP);
};

interface rtcpFbItem { id: string; params: string[]; }
interface AbilityAudioCodec {
  codec: string;
  fmtp: string;
  payload: number;
  rate: number | undefined;
  rtx: number;

  channels?: number;
  rtcpfb: rtcpFbItem[]; // sdp-transform 的字段是 rtcpFb，后台使用的是忽略大小写的，为了保持和后台一致，此处用 rtcpfb
}

export interface AbilityVideoCodec {
  payload: number;
  codec: string;
  fmtp: string;
  rate: number | undefined;
  rtx: number;
  rtcpfb: rtcpFbItem[]
}

export interface ClientAbility {
  ice: {
    ufrag: string,
    password: string
  },
  dtls: {
    hash: string,
    fingerprint: string,
    setup: string // active actpass passive, sdk 默认服务端
  },
  audio: {
    codecs: AbilityAudioCodec[],
    extensions: { id: number; uri: string; }[]
  },
  video: {
    codecs: AbilityVideoCodec[],
    decoders: AbilityVideoCodec[],
    extensions: { id: number; uri: string; }[]
  },
  useDataChannel: boolean;
  playoutDelay?: {
    min: number;
    max: number;
  }
  useDtx?: boolean;
}

export interface ServerAbility {
  audio: {
    codecs: AbilityAudioCodec[];
    extensions: { id: number; uri: string; }[]
  }
  video: {
    codecs: AbilityVideoCodec[],
    decoders: AbilityVideoCodec[],
    extensions: { id: number; uri: string; }[]
  },
  candidates: { ip: string; foundation: string; port: number; priority: number; protocol: string; type: string }[]
  useVp8: boolean; // 开启后，当上行不支持 h264 支持 vp8 时，会使用 vp8 编码。
  forceAllVp8: boolean; // 开启后，当上行支持 vp8 时，会使用 vp8 编解码。
  useH265: boolean; // 开启后，当上行支持 h265 时，会使用 h265 编码。
  setupServerRole: boolean;
  useDataChannel: boolean
  ice: {
    ufrag: string,
    password: string
  },
  dtls: {
    hash: string,
    fingerprint: string,
    setup: string // active actpass passive, sdk 默认服务端
  },
}

type TransformMediaSession = {
  type: string;
  port: number;
  protocol: string;
  payloads?: string | undefined;
} & transform.MediaDescription

/**
 * 从 fmtp 中提取 profile-level-id
 */
export function extractProfileLevelIdFromFmtp(fmtp: string): string | null {
  const match = /profile-level-id=([0-9a-fA-F]{6})/.exec(fmtp);
  return match?.[1]?.toLowerCase() ?? null;
}

/**
 * 根据 profile-level-id 获取 H264 profile 类型
 */
export function getH264ProfileByProfileLevelId(profileLevelId: string): 'baseline' | 'main' | 'high' | 'unknown' {
  const hex = profileLevelId.toLowerCase();
  if (!/^[0-9a-f]{6}$/.test(hex)) return 'unknown';
  const profileIdc = parseInt(hex.slice(0, 2), 16);

  // RFC 6184: profile-level-id = profile_idc (8) + profile_iop (8) + level_idc (8)
  // - Baseline profile_idc: 66 (0x42)
  // - Main profile_idc: 77 (0x4d)
  // - High profile_idc: 100 (0x64)
  if (profileIdc === 0x42) return 'baseline';
  if (profileIdc === 0x4d) return 'main';
  if (profileIdc === 0x64) return 'high';
  return 'unknown';
}

/**
 * 将云控/入参的 profileLevelId 配置解析为实际可用的 6 位 hex
 *
 * - 若 value 是 6 位 hex，则直接返回
 * - 若 value 是 'high'/'baseline'/'main' 字符串，则根据 profile-level-id 的前 1 字节（profile_idc）在本机能力里筛选并选择一个
 *   选择策略：命中第一个即可（按本机能力顺序）
 */
export function convertProfileLevelIdToHex(value: string, clientAbility: ClientAbility | null): string {
  if (!clientAbility) return '';
  const v = value
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
  if (!v) return '';
  if (/^[0-9a-f]{6}$/.test(v)) return v;

  if (v !== 'baseline' && v !== 'main' && v !== 'high') return '';

  for (const codec of clientAbility.video.codecs) {
    const id = extractProfileLevelIdFromFmtp(codec.fmtp);
    if (!id) continue;
    const profile = getH264ProfileByProfileLevelId(id);
    if (profile !== v) continue;
    return id;
  }
  return '';
}
